import type { StarterSyncReport } from '@racemap/sdk/schema/starters';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { Button, Space, Typography } from 'antd';
import { produce } from 'immer';
import { toast } from 'src/lib/toast';
import type { StoreApi } from 'zustand';
import { trackEvent } from '../../../lib/googleAnalytics';
import type { DraftState, State } from '../../reducers';
import type { EventsActions } from '../events_reducers';
import { logAndToast, withSavingState } from './helpers';

const apiClient = RacemapAPIClient.fromWindowLocation();
const { Title } = Typography;

function showSyncReportToast(report: StarterSyncReport, openReport: () => void) {
  const getToastContent = (title: string, description: string) => (
    <Space orientation="vertical">
      <Title level={5} style={{ margin: 0 }}>
        {title}
      </Title>
      {description}
      <Button block variant="link" size="small" onClick={openReport}>
        Show report
      </Button>
    </Space>
  );

  if (report.status === 'success') {
    toast.success(getToastContent('Sync successful', 'Starters synced successfully.'));
  } else if (report.status === 'skipped') {
    toast.info(getToastContent('Sync skipped', 'No starters needed to be synced.'));
  } else if (report.status === 'partial') {
    toast.warn(getToastContent('Partial failure', 'Some starters could not be synced.'));
  } else {
    toast.error(getToastContent('Sync failed', 'The starters could not be synced.'));
  }
}

export const createStarterSyncActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<EventsActions, 'syncStartersOfEvent'> => ({
  syncStartersOfEvent: async ({ eventId, showToast = true } = {}) => {
    const targetId = eventId || get().events.currentEventId;
    if (targetId == null) {
      toast.error('No event selected to sync the starters.');
      return;
    }

    const report = await withSavingState(
      set,
      async () => {
        const res = await apiClient.syncStarterImports({ eventIds: [targetId] });
        // partial still imported some starters
        if (res.status === 'success' || res.status === 'partial') {
          trackEvent('participants_imported', { source: 'sync' });
        }
        await get().events.actions.loadCurrentStarters(targetId);

        set(
          produce((s: DraftState) => {
            s.events.lastStarterSyncReport = res;
          }),
        );
        return res;
      },
      logAndToast('Failed to sync starters', 'The attempt to sync the starters failed.'),
    );

    if (report == null || !showToast) return;
    showSyncReportToast(report, () => get().events.actions.openStarterSyncModal());
  },
});
